import { cn } from "@/lib/utils";
import { Button } from "@/components/shadcn/button";
import { useEffect, useState } from "react";
import {
  Bold,
  Italic,
  CodeXml,
  List,
  Braces,
  Copy,
  Maximize2,
  LucideIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { Separator } from "@/components/shadcn/separator";
import { SpotToast } from "@/components/SpotToast";
import { getModifierKey } from "@/utils/generic";
import type { NodeOption } from "@/components/SpotflowV2/Sidebar/variableOptions";
import { VariablePickerPanel } from "@/components/SpotflowV2/Sidebar/VariablePickerPanel";

interface ToolbarProps {
  variableOptions: NodeOption[];
  // Shown whenever the field has upstream inputs, even if none is compatible,
  // so it matches the "Insert / for a variable value" hint under the editor.
  showVariablePicker: boolean;
  onInsertVariable: (id: string) => void;
  onToggleBold: () => void;
  onToggleItalic: () => void;
  onToggleCodeBlock: () => void;
  onToggleBulletList: () => void;
  isBold: boolean;
  isItalic: boolean;
  isCodeBlock: boolean;
  isBulletList: boolean;
  isRaw: boolean;
  onToggleRaw: () => void;
  // Hides the raw-markdown toggle — for fields whose raw value is already
  // reachable another way (e.g. a code-mode switch on the same field).
  hideRawToggle?: boolean;
  getMarkdown: () => string;
  onExpand?: () => void;
  isDisabled?: boolean;
  // Optional content rendered at the start of the right-hand button group.
  // Used by the AI variant to inject the heuristic score badge.
  extraContent?: React.ReactNode;
}

// Keeps focus in the editor when clicking a toolbar button. Without this,
// `mousedown` on the button moves focus off the editor before the command
// runs, so toggling a mark collapses the selection.
const keepFocus = (e: React.MouseEvent) => e.preventDefault();

export function Toolbar({
  variableOptions,
  showVariablePicker,
  onInsertVariable,
  onToggleBold,
  onToggleItalic,
  onToggleCodeBlock,
  onToggleBulletList,
  isBold,
  isItalic,
  isCodeBlock,
  isBulletList,
  isRaw,
  onToggleRaw,
  hideRawToggle,
  getMarkdown,
  onExpand,
  isDisabled,
  extraContent,
}: ToolbarProps) {
  return (
    <div
      aria-disabled={isDisabled}
      className="flex items-center justify-between gap-4 border-b border-input bg-background2 px-3 py-1.5"
      onMouseDown={keepFocus}
    >
      <div className="flex items-center gap-1">
        <ToolbarButton
          icon={Bold}
          label={`Bold (${getModifierKey()}B)`}
          onClick={onToggleBold}
          isActive={isBold}
          isDisabled={isDisabled || isRaw}
        />
        <ToolbarButton
          icon={Italic}
          label={`Italic (${getModifierKey()}I)`}
          onClick={onToggleItalic}
          isActive={isItalic}
          isDisabled={isDisabled || isRaw}
        />
        <div className="mx-1 h-4 w-px self-center bg-border" />
        <ToolbarButton
          icon={CodeXml}
          label="Code block"
          onClick={onToggleCodeBlock}
          isActive={isCodeBlock}
          isDisabled={isDisabled || isRaw}
        />
        <ToolbarButton
          icon={List}
          label="Bullet list"
          onClick={onToggleBulletList}
          isActive={isBulletList}
          isDisabled={isDisabled || isRaw}
        />
      </div>

      <div className="flex items-center gap-2">
        {extraContent}
        {showVariablePicker && !isRaw && (
          <>
            <InsertVariableDropdown
              options={variableOptions}
              onSelect={onInsertVariable}
              isDisabled={isDisabled}
            />
            <Separator orientation="vertical" />
          </>
        )}
        <div className="flex items-center gap-1">
          {!hideRawToggle && (
            <ToolbarButton
              icon={CodeXml}
              label={isRaw ? "Show rich text" : "Show raw markdown"}
              onClick={onToggleRaw}
              isActive={isRaw}
              isDisabled={isDisabled}
            />
          )}
          <ToolbarButton
            icon={Copy}
            label={`Copy (${getModifierKey()}C)`}
            onClick={() => {
              const md = getMarkdown();
              navigator.clipboard.writeText(md);
              SpotToast.success("Copied to clipboard");
            }}
            isDisabled={isDisabled}
          />
          {onExpand && (
            <ToolbarButton
              icon={Maximize2}
              label="Expand"
              onClick={onExpand}
              isDisabled={isDisabled}
            />
          )}
        </div>
      </div>
    </div>
  );
}

interface ToolbarButtonProps {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  isActive?: boolean;
  isDisabled?: boolean;
}

function ToolbarButton({
  icon: Icon,
  label,
  onClick,
  isActive,
  isDisabled,
}: ToolbarButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="iconsm"
          onClick={onClick}
          className={cn(isActive && "bg-accent text-accent-foreground")}
          isDisabled={isDisabled}
          tabIndex={-1}
        >
          <Icon className="size-4" strokeWidth={1.25} />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

interface InsertVariableDropdownProps {
  options: NodeOption[];
  onSelect: (id: string) => void;
  isDisabled?: boolean;
}

export function InsertVariableDropdown({
  options,
  onSelect,
  isDisabled,
}: InsertVariableDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (open) {
      setSearch("");
      setActiveIndex(0);
    }
  }, [open]);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild disabled={isDisabled}>
            <Button
              variant="ghost"
              size="iconsm"
              isDisabled={isDisabled}
              tabIndex={-1}
            >
              <Braces className="size-4" strokeWidth={1.25} />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">Insert variable</TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        align="end"
        className="w-[330px] p-0 overflow-hidden"
        onCloseAutoFocus={(e) => e.preventDefault()}
        onMouseDown={(e) => {
          if (!(e.target instanceof HTMLInputElement)) e.preventDefault();
        }}
      >
        <VariablePickerPanel
          options={options}
          search={search}
          onSearchChange={setSearch}
          activeIndex={activeIndex}
          onActiveIndexChange={setActiveIndex}
          autoFocusSearch={open}
          onSelect={({ value }) => {
            setOpen(false);
            onSelect(value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              setOpen(false);
            }
          }}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
